import React from "react";
import {
  CheckCircle2,
  Circle,
  AlertCircle,
  Loader2,
  ExternalLink,
  Rocket,
  RotateCcw,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface ProgressStep {
  step: string;
  status: "pending" | "done" | "error" | string;
  message: string;
  id?: string;
}

interface LaunchResult {
  success: boolean;
  campaignId?: string;
  adSetId?: string;
  dupAdSetUrl?: string;
  results?: { step: string; id?: string; error?: string }[];
  message: string;
}

interface CampaignLaunchLogProps {
  progress: ProgressStep[];
  isLaunching: boolean;
  launchResult: LaunchResult | null;
  adAccountId: string;
  onNewCampaign?: () => void;
}

const prefersReducedMotion =
  typeof window !== "undefined"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

const stepVariants = {
  hidden: { opacity: 0, x: prefersReducedMotion ? 0 : -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: prefersReducedMotion ? ("tween" as const) : ("spring" as const),
      stiffness: 500,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    x: prefersReducedMotion ? 0 : -10,
    transition: { duration: 0.15 },
  },
};

const listVariants = {
  hidden: { opacity: 0, height: 0, overflow: "hidden" as const },
  visible: {
    height: "auto",
    opacity: 1,
    overflow: "visible" as const,
    transition: {
      duration: 0.3,
      staggerChildren: prefersReducedMotion ? 0 : 0.06,
      when: "beforeChildren" as const,
      ease: [0.2, 0.65, 0.3, 0.9],
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    overflow: "hidden" as const,
    transition: { duration: 0.2, ease: [0.2, 0.65, 0.3, 0.9] },
  },
};

const iconVariants = {
  initial: { opacity: 0, scale: 0.7, rotate: -10 },
  animate: { opacity: 1, scale: 1, rotate: 0 },
  exit: { opacity: 0, scale: 0.7, rotate: 10 },
  transition: { duration: 0.2, ease: [0.2, 0.65, 0.3, 0.9] },
};

function StepIcon({ status }: { status: string }) {
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={status}
        initial="initial"
        animate="animate"
        exit="exit"
        variants={iconVariants}
        className="flex-shrink-0"
      >
        {status === "done" ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
        ) : status === "pending" ? (
          <Loader2 className="w-3.5 h-3.5 text-orange-400 animate-spin" />
        ) : status === "error" ? (
          <AlertCircle className="w-3.5 h-3.5 text-red-400" />
        ) : (
          <Circle className="w-3.5 h-3.5 text-white/20" />
        )}
      </motion.span>
    </AnimatePresence>
  );
}

export function CampaignLaunchLog({
  progress,
  isLaunching,
  launchResult,
  adAccountId,
  onNewCampaign,
}: CampaignLaunchLogProps) {
  if (progress.length === 0) return null;

  const adsManagerUrl = launchResult?.campaignId
    ? `https://www.facebook.com/adsmanager/manage/campaigns?act=${adAccountId.replace("act_", "")}&campaign_ids=${launchResult.campaignId}`
    : null;

  return (
    <motion.div
      className="rounded-xl border bg-[#08080d] border-white/[0.08] overflow-hidden"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.2, 0.65, 0.3, 0.9] } }}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-white/[0.06]">
        <div
          className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isLaunching
              ? "bg-gradient-to-br from-red-600/30 to-orange-500/30"
              : launchResult?.success
                ? "bg-green-500/20"
                : "bg-red-500/20"
          }`}
        >
          {isLaunching ? (
            <Loader2 className="w-3.5 h-3.5 text-orange-400 animate-spin" />
          ) : launchResult?.success ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
          ) : (
            <AlertCircle className="w-3.5 h-3.5 text-red-400" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">
            {isLaunching
              ? "Tworzę kampanię na Meta..."
              : launchResult?.success
                ? "Kampania utworzona pomyślnie"
                : "Tworzenie zakończone z błędami"}
          </p>
          {isLaunching && (
            <p className="text-[10px] text-white/30 mt-0.5">
              Poczekaj — łączymy się z Meta Ads API
            </p>
          )}
        </div>

        {/* Minimal progress fraction */}
        {isLaunching && (
          <span className="text-[10px] font-mono text-white/30 flex-shrink-0">
            {progress.filter((p) => p.status === "done").length}/{progress.length}
          </span>
        )}
      </div>

      {/* Step list */}
      <div className="px-4 py-3">
        {/* Vertical connector line */}
        <div className="relative">
          <div className="absolute left-[6px] top-2 bottom-2 w-px bg-white/[0.06]" />

          <AnimatePresence initial={false}>
            <motion.ul
              className="space-y-0.5"
              variants={listVariants}
              initial="hidden"
              animate="visible"
            >
              {progress.map((step, i) => (
                <motion.li
                  key={`${step.step}-${i}`}
                  className="flex items-center gap-2.5 pl-1 py-1 rounded-md group"
                  variants={stepVariants}
                  whileHover={{
                    backgroundColor: "rgba(255,255,255,0.02)",
                    transition: { duration: 0.15 },
                  }}
                >
                  <StepIcon status={step.status} />

                  <span
                    className={`text-xs leading-snug font-mono transition-colors ${
                      step.status === "done"
                        ? "text-white/35 line-through decoration-white/15"
                        : step.status === "error"
                          ? "text-red-400"
                          : step.status === "pending"
                            ? "text-white/80"
                            : "text-white/40"
                    }`}
                  >
                    {step.message}
                  </span>

                  {step.id && step.status === "done" && (
                    <motion.span
                      className="ml-auto text-[9px] font-mono text-white/20 flex-shrink-0 truncate max-w-[90px]"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1, transition: { delay: 0.2 } }}
                    >
                      {step.id}
                    </motion.span>
                  )}
                </motion.li>
              ))}
            </motion.ul>
          </AnimatePresence>
        </div>
      </div>

      {/* Final result footer */}
      <AnimatePresence>
        {launchResult && (
          <motion.div
            className={`px-4 py-3 border-t ${
              launchResult.success
                ? "border-green-500/15 bg-green-500/5"
                : "border-red-500/15 bg-red-500/5"
            }`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto", transition: { duration: 0.25 } }}
            exit={{ opacity: 0, height: 0 }}
          >
            <p
              className={`text-xs font-medium ${
                launchResult.success ? "text-green-400" : "text-red-400"
              }`}
            >
              {launchResult.message}
            </p>

            <div className="flex flex-wrap gap-3 mt-2">
              {adsManagerUrl && (
                <a
                  href={adsManagerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-1.5 rounded-lg bg-gradient-to-r from-red-600/80 to-orange-500/80 hover:from-red-600 hover:to-orange-500 transition-all"
                >
                  <Rocket className="w-3 h-3" />
                  Otwórz w Ads Manager
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}

              {launchResult.dupAdSetUrl && (
                <a
                  href={launchResult.dupAdSetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-purple-300 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/25 hover:bg-purple-500/20 transition-all"
                >
                  Otwórz duplikat ad setu
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}

              {launchResult.success && onNewCampaign && (
                <button
                  type="button"
                  onClick={onNewCampaign}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/70 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:text-white transition-all"
                >
                  <RotateCcw className="w-3 h-3" />
                  Stwórz nową kampanię
                </button>
              )}
            </div>

            {/* "Co dalej?" — next steps after successful launch */}
            {launchResult.success && (
              <div className="mt-3 p-3 rounded-lg bg-emerald-500/[0.04] border border-emerald-500/20">
                <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-300/80 mb-2">
                  Co dalej?
                </p>
                <ol className="space-y-1.5 text-[11px] text-white/70">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-400 font-bold w-4 shrink-0">1.</span>
                    <span>
                      Otwórz <span className="text-white">Ads Manager</span> i sprawdź ustawienia
                      kampanii (placement, audiencja, budżet).
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-400 font-bold w-4 shrink-0">2.</span>
                    <span>
                      Po review włącz kampanię w Ads Managerze (suwak{" "}
                      <span className="text-white">Off → On</span> obok kampanii).
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-400 font-bold w-4 shrink-0">3.</span>
                    <span>
                      Po 24-48h pierwsze dane — sprawdź{" "}
                      <span className="text-white">CPC, CPL, CTR</span>. Jeśli słabe wyniki, powtórz
                      z innymi kreatywami.
                    </span>
                  </li>
                </ol>
              </div>
            )}

            {/* Per-step result details */}
            {launchResult.results && launchResult.results.length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {launchResult.results.map((r, i) => (
                  <li key={i} className="text-[10px] font-mono">
                    {r.error ? (
                      <span className="text-red-400">
                        ✗ {r.step}: {r.error}
                      </span>
                    ) : (
                      <span className="text-green-400/60">
                        ✓ {r.step}: {r.id}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
